import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Building2, Sparkles, Gift, CreditCard, CheckCircle2, ArrowRight, Loader2, ShieldCheck, Clock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_BENEFITS = [
  'Unlimited residents (10 during trial)',
  'AI-powered maintenance triage',
  'Notices, documents, venue bookings',
  'Visitor management & gate logs',
  'Trustee dashboard + voting',
  'AGM hosting + audit trail',
  'Email + WhatsApp notifications',
  'Mobile app (Android + iOS)',
];

export default function BuildingActivate() {
  const { hoaId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [building, setBuilding] = useState(state?.building || null);
  const [loading, setLoading] = useState(!state?.building);
  const [busy, setBusy] = useState(null); // 'trial' | 'pay'

  const token = localStorage.getItem('session_token');

  useEffect(() => {
    if (building) return;
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/hoa/${hoaId}`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (active) setBuilding(data);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [building, hoaId, token]);

  const startTrial = async () => {
    setBusy('trial');
    try {
      // The building is created in trial mode by default — we just confirm + route into the workspace.
      toast.success('Trial activated — you have 30 days to explore.');
      navigate(user?.role === 'managing_agent' ? '/agent' : '/admin');
    } catch (e) {
      toast.error('Could not start trial');
    } finally {
      setBusy(null);
    }
  };

  const payNow = async () => {
    setBusy('pay');
    try {
      // Pay-now is not yet wired to a live gateway. Direct to the existing
      // billing intent flow; the user can complete payment manually for now.
      toast.info('Redirecting to billing…');
      navigate(`/billing?building=${hoaId}&intent=activate`);
    } catch (e) {
      toast.error('Could not start payment');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-emerald-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium mb-4">
            <CheckCircle2 className="w-3.5 h-3.5" /> Building created
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {building?.name || 'Your building'} is ready
          </h1>
          <p className="mt-2 text-slate-500">Pick a plan to start running it on BodyCorp365.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* TRIAL CARD */}
          <Card className="border-2 border-indigo-200 hover:border-indigo-400 transition-colors relative overflow-hidden" data-testid="trial-card">
            <div className="absolute top-3 right-3">
              <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100"><Gift className="w-3 h-3 mr-1" /> Recommended</Badge>
            </div>
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-3">
                <Sparkles className="w-6 h-6 text-indigo-600" />
              </div>
              <CardTitle className="text-2xl">Start your 30-day free trial</CardTitle>
              <CardDescription>Explore every feature. No card required. Cancel anytime.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-semibold tracking-tight">R0</span>
                <span className="text-slate-500 text-sm">/ first 30 days</span>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                {PLAN_BENEFITS.slice(0, 5).map((b) => (
                  <li key={b} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{b}</li>
                ))}
                <li className="flex gap-2 text-slate-400"><Clock className="w-4 h-4 shrink-0 mt-0.5" />Activate later to unlock unlimited residents & premium reports</li>
              </ul>
              <Button
                className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700"
                onClick={startTrial}
                disabled={busy !== null}
                data-testid="start-trial-btn"
              >
                {busy === 'trial' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Start free trial
              </Button>
            </CardContent>
          </Card>

          {/* PAY CARD */}
          <Card className="border-2 border-emerald-200 hover:border-emerald-400 transition-colors" data-testid="pay-card">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-3">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl">Pay now &amp; activate</CardTitle>
              <CardDescription>Skip the trial. Go live today with the full feature set unlocked.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-semibold tracking-tight">R{building?.total_units ? Math.max(15 * Number(building.total_units), 250) : 250}</span>
                <span className="text-slate-500 text-sm">/ month · billed monthly</span>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                {PLAN_BENEFITS.map((b) => (
                  <li key={b} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{b}</li>
                ))}
              </ul>
              <Button
                className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700"
                onClick={payNow}
                disabled={busy !== null}
                data-testid="pay-now-btn"
              >
                {busy === 'pay' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Pay now &amp; activate
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-[11px] text-slate-400 text-center flex items-center justify-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Secure payment via PayFast — cancel anytime.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 text-center text-sm text-slate-500">
          <Building2 className="w-4 h-4 inline mr-1" />
          You can switch plans later from your building settings.
        </div>
      </div>
    </div>
  );
}
